/**
 * Zod schemas for waitlist_entries + invite_tokens DB shapes.
 *
 * Spec authority: `.plan/features/2026-04-24-admin-beta-feedback.md §4.2 + §4.3`
 * Architecture ref: `architecture.md §4.7` (D1 global schema additions).
 *
 * Pure TS + Zod — no fs / node / DOM imports (package purity invariant).
 */

import { z } from "zod";
import { UUID_RE } from "../urls/types.js";

const uuid = z.string().regex(UUID_RE);
const timestamp = z.number().int().nonnegative();

// ── WaitlistStatus ────────────────────────────────────────────────────────────

export const WaitlistStatusSchema = z.enum([
  "pending",
  "approved",
  "rejected",
  "invited",
  "redeemed",
]);

export type WaitlistStatus = z.infer<typeof WaitlistStatusSchema>;

// ── WaitlistEntry (DB row shape) ──────────────────────────────────────────────

export const WaitlistEntrySchema = z
  .object({
    uuid,
    email: z.string().email(),
    reason: z.string().nullable().optional(),
    referrer: z.string().nullable().optional(),
    turnstile_verdict: z.string(),
    ip_hash: z.string(),
    status: WaitlistStatusSchema,
    created_at: timestamp,
    decided_at: timestamp.nullable().optional(),
    decided_note: z.string().nullable().optional(),
    purge_at: timestamp.nullable().optional(),
  })
  .strict();

export type WaitlistEntry = z.infer<typeof WaitlistEntrySchema>;

// ── InviteToken (DB row shape — raw bearer token is never persisted) ──────────

export const InviteTokenSchema = z
  .object({
    token_hash: z.string().min(1), // SHA-256 hex; raw token only sent in email
    waitlist_uuid: uuid.nullable().optional(),
    email_target: z.string().email(),
    created_at: timestamp,
    expires_at: timestamp,
    redeemed_at: timestamp.nullable().optional(),
    redeemed_by_user_uuid: uuid.nullable().optional(),
    revoked_at: timestamp.nullable().optional(),
    purge_at: timestamp.nullable().optional(),
  })
  .strict();

export type InviteToken = z.infer<typeof InviteTokenSchema>;

// ── InviteMintRequest (admin-direct mint input) ───────────────────────────────

export const InviteMintRequestSchema = z
  .object({
    email: z.string().email(),
    note: z.string().max(500).nullable().optional(),
  })
  .strict();

export type InviteMintRequest = z.infer<typeof InviteMintRequestSchema>;
